import { Alert, Button, Fieldset, Group, Loader, Select, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconTrash } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useLocation, useMatchRoute } from "@tanstack/react-router";
import { Suspense, useEffect } from "react";
import { senderPageQuery } from "routes/_auth/organization/senders/route";
import { httpPostGraphql } from "shared/api/httpClient";
import { useArchiveSenderModal } from "shared/components/organization/senders/useArchiveSender";
import { GroupSearchCombobox } from "shared/components/organization/users/GroupSearchCombobox";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { useAside } from "shared/utils/aside";
import { to } from "shared/utils/fns";
import { logger } from "shared/utils/logger";
import { notify } from "shared/utils/notify";
import { graphql } from "src/gql";
import { queryClient } from "src/queryClient";

const SenderDetailsContainer = ({ senderId }: { senderId: string }) => {
    const aside = useAside();

    const { data } = useSuspenseQuery(senderPageQuery);
    const sender = data.senders.find((it) => it.id == senderId);

    if (!sender)
        return (
            <Alert color="red" icon={<IconAlertCircle />}>
                Can't find sender!
            </Alert>
        );

    const {
        data: { getAvailableEmails: availableEmails },
    } = useSuspenseQuery(sendersPageContactOptionsQueries.emails);
    const {
        data: { getAvailablePhoneNumbers: availablePhoneNumbers },
    } = useSuspenseQuery(sendersPageContactOptionsQueries.phoneNumbers);

    const updateSender = useGraphqlMutation(muts.updateSender);
    const { openArchiveModal, archiveIsPending } = useArchiveSenderModal({
        sender,
        onSuccess: () => {
            aside.dismiss();
            queryClient.invalidateQueries({ queryKey: ["senders", "table"] }).catch(notify.catch);
        },
    });

    const form = useForm({
        mode: "uncontrolled",
        initialValues: {
            id: sender.id,
            name: sender.name,
            group: sender.group,
            email: sender.fromEmail ?? "",
            phoneNumber: sender.fromPhone ?? "",
        },
        validate: {
            name: (it: string) => (it.length == 0 ? "Required" : null),
        },
    });

    const { group } = form.getValues();

    useEffect(() => {
        const values = form.getValues();
        if (values.id == sender.id) return;

        form.setInitialValues({ ...sender, email: sender.fromEmail ?? "", phoneNumber: sender.fromPhone ?? "" });
        form.reset();
    }, [sender]);

    return (
        <form
            onSubmit={form.onSubmit((values, e) => {
                e?.preventDefault();
                updateSender
                    .mutateAsync({
                        id: values.id,
                        params: {
                            name: values.name,
                            fromEmailAddress: values.email,
                            fromPhone: values.phoneNumber,
                            verificationSender: sender.verificationSender,
                            group: values.group.id,
                        },
                    })
                    .then(() => {
                        form.setInitialValues({
                            ...form.getValues(),
                        });
                        form.reset();
                        notify.show.success("Updated sender");
                        return queryClient.invalidateQueries({ queryKey: ["senders", "table"] });
                    })
                    .catch(notify.catch);
            })}
            style={{ height: "100%" }}
        >
            <Fieldset variant="unstyled" h="100%">
                <Stack h="100%" justify="space-between">
                    <Stack>
                        <TextInput
                            required
                            label="Name"
                            placeholder="Enter name..."
                            key={form.key("name")}
                            {...form.getInputProps("name")}
                        />
                        <GroupSearchCombobox
                            required
                            fullWidth
                            selectedGroup={group}
                            setSelectedGroup={(g) => form.setFieldValue("group", g)}
                            nullable={false}
                        />
                        <Select
                            required
                            label="Email"
                            allowDeselect={false}
                            data={Array.from(new Set(availableEmails.map(to("email"))))}
                            key={form.key("email")}
                            {...form.getInputProps("email")}
                        />
                        <Select
                            required
                            label="Phone Number"
                            allowDeselect={false}
                            data={Array.from(new Set(availablePhoneNumbers))}
                            key={form.key("phoneNumber")}
                            {...form.getInputProps("phoneNumber")}
                        />
                        <Button
                            leftSection={<IconTrash size={16} />}
                            onClick={() => openArchiveModal()}
                            loading={archiveIsPending}
                            disabled={sender.isSystem}
                        >
                            Archive
                        </Button>
                    </Stack>
                    <Group justify="flex-end">
                        <Button variant="default" onClick={() => aside.dismiss()}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={!form.isDirty()} loading={updateSender.isPending}>
                            Save
                        </Button>
                    </Group>
                </Stack>
            </Fieldset>
        </form>
    );
};

function Page() {
    const aside = useAside();
    const nav = Route.useNavigate();
    const { href } = useLocation();
    const { senderId } = Route.useParams();
    const matchRoute = useMatchRoute();

    useEffect(() => {
        if (!matchRoute({ to: "/organization/senders" })) aside.dismiss({ silent: true });
    }, [href]);

    useEffect(() => {
        aside.present({
            sticky: true,
            label: "Edit Sender",
            component: (
                <Suspense fallback={<Loader />}>
                    <SenderDetailsContainer senderId={senderId} />
                </Suspense>
            ),
            didDismiss: () => void nav({ to: "/organization/senders" }),
        });
    }, [senderId]);

    return null;
}

/** @public */
export const Route = createFileRoute("/_auth/organization/senders/$senderId")({
    loader: () => {
        queryClient.ensureQueryData(senderPageQuery).catch(logger.error);
    },
    component: () => (
        <Suspense fallback={<Loader />}>
            <Page />
        </Suspense>
    ),
});

export const sendersPageContactOptionsQueries = {
    phoneNumbers: queryOptions({
        queryKey: ["senders", "phoneNumbers"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query SenderPhoneNumbers {
                        getAvailablePhoneNumbers
                    }
                `),
                {},
            ),
    }),
    emails: queryOptions({
        queryKey: ["senders", "emails"],
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query SenderEmails {
                        getAvailableEmails {
                            email
                        }
                    }
                `),
                {},
            ),
    }),
};

const muts = {
    updateSender: graphql(`
        mutation SendersUpdateSender($id: UUID!, $params: UpdateSenderParametersInput!) {
            updateSender(id: $id, params: $params) {
                id
            }
        }
    `),
};
